// 宣言的なショートカット表。ヘルプ画面はこの表から生成する。
// keydown/keyup の両方を扱い、Space 押下中の手のひらツールなど一時切替も担う。

import { onMounted, onBeforeUnmount, ref } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useUiStore, type ToolId, IMPLEMENTED_TOOLS } from '@/stores/uiStore'
import { isModalOpen } from './useModalStack'
import * as actions from '@/app/actions'
import { runCommand } from '@/commands/registry'
import type { I18nKey } from '@/i18n'

export interface Shortcut {
  id: string
  /** 'mod+z' のような表記。mod = Cmd (mac) / Ctrl */
  keys: string[]
  label: I18nKey
  run: () => void
  /** ドキュメントが必要か */
  needsDocument?: boolean
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

export const TOOL_KEYS: Record<string, ToolId> = { v: 'move', h: 'hand', z: 'zoom', m: 'marquee', l: 'lasso', w: 'wand', c: 'crop', i: 'eyedropper', b: 'brush', e: 'eraser', g: 'fill', t: 'text', u: 'shape', p: 'pen', s: 'clone', j: 'spotHeal', o: 'dodge', r: 'blurTool' }

export const SHORTCUTS: Shortcut[] = [
  { id: 'undo', keys: ['mod+z'], label: 'shortcut.undo', run: actions.undo, needsDocument: true },
  { id: 'redo', keys: ['mod+shift+z', 'mod+y'], label: 'shortcut.redo', run: actions.redo, needsDocument: true },
  { id: 'zoomIn', keys: ['mod+=', 'mod++', 'mod+;'], label: 'shortcut.zoomIn', run: () => useUiStore().zoomStep(1), needsDocument: true },
  { id: 'zoomOut', keys: ['mod+-'], label: 'shortcut.zoomOut', run: () => useUiStore().zoomStep(-1), needsDocument: true },
  { id: 'fit', keys: ['mod+0'], label: 'shortcut.fit', run: actions.zoomFit, needsDocument: true },
  { id: 'actual', keys: ['mod+1'], label: 'shortcut.actual', run: actions.zoomActual, needsDocument: true },
  { id: 'new', keys: ['mod+n'], label: 'shortcut.new', run: () => (useUiStore().dialog = 'new') },
  { id: 'open', keys: ['mod+o'], label: 'shortcut.open', run: () => void actions.openFilePicker() },
  { id: 'save', keys: ['mod+s'], label: 'shortcut.save', run: () => void actions.saveProject(), needsDocument: true },
  { id: 'export', keys: ['mod+shift+e', 'mod+alt+shift+s'], label: 'shortcut.export', run: () => (useUiStore().dialog = 'export'), needsDocument: true },
  { id: 'newLayer', keys: ['mod+shift+n'], label: 'shortcut.newLayer', run: () => useDocumentStore().addEmptyLayer(), needsDocument: true },
  { id: 'deleteLayer', keys: ['delete', 'backspace'], label: 'shortcut.clear', run: actions.deleteOrClear, needsDocument: true },
  { id: 'selectAll', keys: ['mod+a'], label: 'shortcut.selectAll', run: () => useDocumentStore().selectAll(), needsDocument: true },
  { id: 'deselect', keys: ['mod+d'], label: 'shortcut.deselect', run: () => useDocumentStore().deselect(), needsDocument: true },
  { id: 'invert', keys: ['mod+shift+i'], label: 'shortcut.invert', run: () => useDocumentStore().invertSelection(), needsDocument: true },
  { id: 'copy', keys: ['mod+c'], label: 'shortcut.copy', run: () => void actions.copySelection(), needsDocument: true },
  { id: 'cut', keys: ['mod+x'], label: 'shortcut.cut', run: () => void actions.cutSelection(), needsDocument: true },
  { id: 'paste', keys: ['mod+v'], label: 'shortcut.paste', run: () => void actions.pasteAsLayer() },
  { id: 'freeTransform', keys: ['mod+t'], label: 'shortcut.freeTransform', run: actions.startFreeTransform, needsDocument: true },
  { id: 'imageSize', keys: ['mod+alt+i'], label: 'shortcut.imageSize', run: () => (useUiStore().dialog = 'imageSize'), needsDocument: true },
  { id: 'canvasSize', keys: ['mod+alt+c'], label: 'shortcut.canvasSize', run: () => (useUiStore().dialog = 'canvasSize'), needsDocument: true },
  { id: 'quickMask', keys: ['q'], label: 'shortcut.quickMask', run: () => { const ui = useUiStore(); ui.quickMask = !ui.quickMask }, needsDocument: true },
  { id: 'rulers', keys: ['mod+r'], label: 'shortcut.rulers', run: () => { const ui = useUiStore(); ui.showRulers = !ui.showRulers } },
  { id: 'grid', keys: ["mod+'"], label: 'shortcut.grid', run: () => { const ui = useUiStore(); ui.showGrid = !ui.showGrid } },
  { id: 'guides', keys: ['mod+;'], label: 'shortcut.guides', run: () => { const ui = useUiStore(); ui.showGuides = !ui.showGuides } },
  { id: 'brushBigger', keys: [']'], label: 'shortcut.brushSize', run: () => useUiStore().adjustBrushSize(1) },
  { id: 'brushSmaller', keys: ['['], label: 'shortcut.brushSize', run: () => useUiStore().adjustBrushSize(-1) },
  { id: 'swapColors', keys: ['x'], label: 'shortcut.swapColors', run: () => useUiStore().swapColors() },
  { id: 'resetColors', keys: ['d'], label: 'shortcut.resetColors', run: () => useUiStore().resetColors() },
  { id: 'mergeDown', keys: ['mod+e'], label: 'shortcut.mergeDown', run: () => void runCommand('layer.mergeDown'), needsDocument: true },
  { id: 'mergeVisible', keys: ['mod+shift+e'], label: 'shortcut.mergeVisible', run: () => void runCommand('layer.mergeVisible'), needsDocument: true },
  { id: 'viaCopy', keys: ['mod+j'], label: 'shortcut.viaCopy', run: () => void runCommand('layer.newViaCopy'), needsDocument: true },
  { id: 'viaCut', keys: ['mod+shift+j'], label: 'shortcut.viaCut', run: () => void runCommand('layer.newViaCopy', { cut: true }), needsDocument: true },
  { id: 'fill', keys: ['shift+f5'], label: 'shortcut.fill', run: () => (useUiStore().dialog = 'fill'), needsDocument: true },
  { id: 'autoTone', keys: ['mod+shift+l'], label: 'shortcut.autoTone', run: () => void runCommand('image.autoTone'), needsDocument: true },
  { id: 'autoContrast', keys: ['mod+shift+alt+l'], label: 'shortcut.autoContrast', run: () => void runCommand('image.autoContrast'), needsDocument: true },
  { id: 'toolGuide', keys: ['f1'], label: 'shortcut.toolGuide', run: () => { const ui = useUiStore(); ui.toolGuideOpen = !ui.toolGuideOpen } },
  { id: 'group', keys: ['mod+g'], label: 'shortcut.group', run: actions.groupActiveLayers, needsDocument: true },
  { id: 'clip', keys: ['mod+alt+g'], label: 'shortcut.clip', run: actions.toggleClipActiveLayer, needsDocument: true }
]

const OVERRIDE_KEY = 'bpe.shortcuts.v1'
function loadOverrides(): Record<string, string[]> {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(OVERRIDE_KEY) : null
    const v = raw ? (JSON.parse(raw) as unknown) : null
    if (!v || typeof v !== 'object' || Array.isArray(v)) return {}
    const out: Record<string, string[]> = {}
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) if (Array.isArray(val) && val.every(x => typeof x === 'string')) out[k] = val as string[]
    return out
  } catch {
    return {}
  }
}
/** ユーザーが変更したキー (id → keys)。空配列は「割り当てなし」 */
export const shortcutOverrides = ref<Record<string, string[]>>(loadOverrides())
function saveOverrides() {
  try {
    localStorage.setItem(OVERRIDE_KEY, JSON.stringify(shortcutOverrides.value))
  } catch {
    /* ignore */
  }
}
export function keysFor(s: Shortcut): string[] {
  return shortcutOverrides.value[s.id] ?? s.keys
}
/** キーを割り当てる。他のショートカットが同じ組み合わせを使っていればそちらから外す */
export function setShortcutKeys(id: string, keys: string[]): void {
  const next = { ...shortcutOverrides.value }
  for (const s of SHORTCUTS) {
    if (s.id === id) continue
    const cur = keysFor(s)
    if (cur.some(k => keys.includes(k))) next[s.id] = cur.filter(k => !keys.includes(k))
  }
  const target = SHORTCUTS.find(s => s.id === id)
  if (target && JSON.stringify(target.keys) === JSON.stringify(keys)) delete next[id]
  else next[id] = keys
  shortcutOverrides.value = next
  saveOverrides()
}
export function resetShortcuts(): void {
  shortcutOverrides.value = {}
  saveOverrides()
}
/** KeyboardEvent を 'mod+shift+k' 形式にする (ダイアログのキャプチャと照合で共用) */
export function comboOf(e: KeyboardEvent): string {
  const parts: string[] = []
  if (e.metaKey || e.ctrlKey) parts.push('mod')
  if (e.shiftKey) parts.push('shift')
  if (e.altKey) parts.push('alt')
  const key = e.key.toLowerCase()
  parts.push(key === ' ' ? 'space' : key)
  return parts.join('+')
}

/** 同じキーを続けて押すと切り替わるツールの組 (Photoshop の W = 自動選択 / クイック選択) */
export const TOOL_CYCLES: ToolId[][] = [['wand', 'quickSelect'], ['spotHeal', 'heal'], ['blurTool', 'sharpenTool', 'smudge'], ['dodge', 'burn', 'sponge'], ['fill', 'gradient']]

export function formatKeys(keys: string): string {
  return keys
    .split('+')
    .map(k => {
      if (k === 'mod') return isMac ? '⌘' : 'Ctrl'
      if (k === 'shift') return isMac ? '⇧' : 'Shift'
      if (k === 'alt') return isMac ? '⌥' : 'Alt'
      if (k === 'delete') return 'Del'
      if (k === 'backspace') return '⌫'
      return k.length === 1 ? k.toUpperCase() : k
    })
    .join(isMac ? '' : '+')
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

export function useShortcuts() {
  const ui = useUiStore()
  const docStore = useDocumentStore()
  let spaceHeld = false

  function onKeyDown(e: KeyboardEvent) {
    if (isTypingTarget(e.target)) return
    if (isModalOpen.value) return
    // テキスト編集中はすべてのショートカットを止める (フォーカス前の打鍵でツールが切り替わらないように)
    if (ui.editingTextId) return
    const combo = comboOf(e)
    if (ui.modalTool && (combo === 'enter' || combo === 'escape' || combo === 'delete' || combo === 'backspace')) return

    if (combo === 'space' && !e.repeat) {
      spaceHeld = true
      ui.temporaryTool = 'hand'
      e.preventDefault()
      return
    }
    if (combo === 'space') {
      e.preventDefault()
      return
    }
    for (const s of SHORTCUTS) {
      if (!keysFor(s).includes(combo)) continue
      if (s.needsDocument && !docStore.hasDocument) return
      e.preventDefault()
      s.run()
      return
    }
    // 単キーのツール切替
    if (!e.metaKey && !e.ctrlKey && !e.altKey) {
      const tool = TOOL_KEYS[e.key.toLowerCase()]
      if (tool && IMPLEMENTED_TOOLS.has(tool)) {
        const cycle = TOOL_CYCLES.find(c => c.includes(tool) && c.includes(ui.activeTool))
        if (cycle) {
          const next = cycle[(cycle.indexOf(ui.activeTool) + 1) % cycle.length]!
          ui.activeTool = IMPLEMENTED_TOOLS.has(next) ? next : tool
        } else ui.activeTool = tool
        e.preventDefault()
      }
    }
  }

  function onKeyUp(e: KeyboardEvent) {
    if (e.key === ' ' && spaceHeld) {
      spaceHeld = false
      ui.temporaryTool = null
    }
  }

  function onBlur() {
    spaceHeld = false
    ui.temporaryTool = null
  }

  onMounted(() => {
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
  })
  onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeyDown)
    window.removeEventListener('keyup', onKeyUp)
    window.removeEventListener('blur', onBlur)
  })
}
